/* =========================================================
   TOTAL FOOTY CO — GLOBAL JS
   No framework, no build step. Progressive enhancement only.

   REWRITTEN 2026-07-28. Defects fixed:

   1. ADD-TO-CART FAILURES WERE SWALLOWED.
      The .catch() did `window.location.href = "/cart"`, throwing away the
      reason. A shopper who tried to buy more than the available stock was
      silently bounced to an unchanged cart with no explanation — the single
      most conversion-damaging bug in the theme. Errors are now surfaced in the
      form's live region, or as a toast when there is no form-level slot
      (product-card quick add).

   2. trapFocus() DID NOT TRAP.
      It focused the first element and returned. Tab then walked straight out
      of the open drawer into the page behind it. There is now a real trap plus
      focus restoration to whatever opened the drawer.

   3. THREE NETWORK ROUND TRIPS PER ADD.
      /cart/add.js -> /cart.js -> /?sections=... Now a single request: the
      Shopify AJAX API renders the sections inline when you pass `sections`.

   4. NO LOADING STATE. The button was disabled but gave no feedback, so slow
      connections read as a dead button and people clicked repeatedly.

   5. CHECKOUT COULD BE DOUBLE-SUBMITTED.

   6. STICKY ADD-TO-CART OBSERVED THE WHOLE FORM, so the bar only appeared
      after the entire form (including the accordion) had scrolled away. It now
      observes the button itself.

   7. THE SCROLL HANDLER WROTE data-scrolled ON EVERY SCROLL EVENT, invalidating
      style on each frame. Now only writes when the state actually changes.

   8. CART MUTATIONS HAD NO .catch(), so a failed quantity change left the UI
      showing a number the server had rejected.

   ---------------------------------------------------------
   PHASE 3 — CART AND CHECKOUT PASS, 2026-07-29:

   9. THE CHECKOUT BUTTON COULD STICK IN ITS LOADING STATE FOREVER.
      The double-submit guard set `btn.dataset.submitted = "true"` and never
      cleared it. Browsers restore a page from the back/forward cache exactly
      as it was left, so: tap Checkout -> change your mind -> press Back ->
      the cart returns with a spinning, permanently dead Checkout button. The
      only escape was a hard reload. Now reset on `pageshow`.

  10. CART UPDATES DESTROYED FOCUS.
      Every quantity change re-renders the whole cart subtree. The element the
      shopper was interacting with was replaced, so focus fell back to <body>:
      a keyboard user lost their place entirely and a screen-reader user was
      dumped at the top of the document. Focus is now captured before the swap
      and restored to the equivalent control after it.

  11. CART UPDATES WERE SILENT. Nothing was announced when a line changed or
      was removed. There is now a polite live region in both cart surfaces.

  12. ONE SHARED DEBOUNCE TIMER FOR ALL LINES. Changing line A then line B
      inside 400ms cancelled line A's request and only line B was sent. The
      debounce is now keyed per line.

  13. THE STEPPER IGNORED `max`. It happily counted past available stock so the
      shopper only discovered the limit when the request failed.

  14. THE SEARCH DRAWER OPENED WITH FOCUS ON ITS CLOSE BUTTON, so opening
      search and typing did nothing. Drawers can now nominate their own
      initial focus target.

  ---------------------------------------------------------
  PHASE 3 CLOSE-OUT, 2026-07-29:

  15. A SOLD-OUT BUTTON BECAME PURCHASABLE AFTER THE FIRST ADD TO CART.
      setLoading(btn, false) called removeAttribute("disabled") unconditionally.
      A sold-out variant renders its button disabled from Liquid, so as soon as
      ANY add-to-cart request finished on that page — including a failed one —
      both the inline and the sticky button were re-enabled and a shopper could
      submit an unavailable variant. The code dispatched a `cart:buttons-reset`
      event that was clearly meant to repair this, but nothing anywhere in the
      theme listened for it, so the state was simply lost. setLoading now
      records the pre-loading disabled state and restores it, and the dead
      event has been removed rather than left as a false reassurance.

  16. A FAILED QUANTITY CHANGE ANNOUNCED SUCCESS.
      /cart/change.js answers 422 with a JSON body carrying `status`, and that
      body still parses cleanly, so the .then() branch ran and announced "Cart
      updated" for a change the server had rejected. Error payloads are now
      detected and routed to the failure path.
   ========================================================= */
(function () {
  "use strict";

  document.documentElement.classList.remove("no-js");
  document.documentElement.classList.add("js");

  /* ---------- Helpers ---------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  function focusableIn(el) {
    return qsa(FOCUSABLE, el).filter(function (n) {
      return !n.hasAttribute("hidden") && n.offsetWidth + n.offsetHeight > 0;
    });
  }

  /* ---------- Toast (used when there is no inline slot for a message) ---------- */
  var toastEl = null;
  var toastTimer = null;
  function toast(message, isError) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "tfc-toast";
      toastEl.setAttribute("role", "status");
      toastEl.setAttribute("aria-live", "polite");
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.toggle("tfc-toast--error", !!isError);
    toastEl.setAttribute("data-visible", "true");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.setAttribute("data-visible", "false");
    }, 5000);
  }

  /* ---------- Polite announcements for cart changes ----------
     The live regions are rendered by sections/cart-drawer.liquid and
     sections/main-cart.liquid. They are re-created on every fragment swap, so
     resolve them at call time rather than caching a node reference. */
  function announce(message) {
    if (!message) return;
    var regions = qsa("[data-cart-live-region]");
    if (!regions.length) return;
    regions.forEach(function (r) { r.textContent = ""; });
    // A region that already holds the same string is not re-announced, and a
    // node written to in the same tick it was inserted may be missed entirely.
    setTimeout(function () {
      qsa("[data-cart-live-region]").forEach(function (r) { r.textContent = message; });
    }, 80);
  }

  /* ---------- Button loading state ----------
     `disabled` is doing two unrelated jobs on the add-to-cart buttons: it marks
     a sold-out variant, and it blocks a double submit while a request is in
     flight. Clearing it after a request therefore has to put back whatever the
     first meaning had set, not blanket-enable the button. */
  function setLoading(btn, on) {
    if (!btn) return;
    if (on) {
      if (!btn.hasAttribute("data-was-disabled")) {
        btn.setAttribute("data-was-disabled", btn.disabled ? "true" : "false");
      }
      btn.classList.add("is-loading");
      btn.setAttribute("aria-busy", "true");
      btn.setAttribute("disabled", "disabled");
    } else {
      btn.classList.remove("is-loading");
      btn.removeAttribute("aria-busy");
      if (btn.getAttribute("data-was-disabled") === "true") {
        btn.setAttribute("disabled", "disabled");
      } else {
        btn.removeAttribute("disabled");
      }
      btn.removeAttribute("data-was-disabled");
    }
  }

  /* ---------- Drawers (cart / search / mobile menu / size guide) ---------- */
  var drawerReturnFocus = {};

  function openDrawer(id, trigger) {
    var drawer = document.getElementById(id);
    if (!drawer) return;
    var overlay = qs('[data-drawer-overlay="' + id + '"]') || qs(".drawer__overlay");

    drawerReturnFocus[id] = trigger || document.activeElement;

    drawer.setAttribute("data-state", "open");
    drawer.setAttribute("aria-hidden", "false");
    drawer.removeAttribute("inert");
    if (overlay) overlay.setAttribute("data-state", "open");
    document.body.style.overflow = "hidden";

    if (trigger) trigger.setAttribute("aria-expanded", "true");

    // A search drawer whose first stop is the close button is useless: you open
    // it to type. Let a drawer nominate its own target, and treat a search
    // input as one by default.
    var preferred = qs("[data-drawer-autofocus]", drawer) || qs("[data-search-input]", drawer);
    if (preferred) {
      preferred.focus();
      if (typeof preferred.select === "function") {
        try { preferred.select(); } catch (e) { /* not a text field */ }
      }
      return;
    }

    var items = focusableIn(drawer);
    var closeBtn = qs("[data-close-drawer]", drawer);
    (closeBtn || items[0] || drawer).focus();
  }

  function closeDrawer(id) {
    var drawer = document.getElementById(id);
    if (!drawer) return;
    var overlay = qs('[data-drawer-overlay="' + id + '"]') || qs(".drawer__overlay");

    drawer.setAttribute("data-state", "closed");
    drawer.setAttribute("aria-hidden", "true");
    // inert keeps the closed panel out of the tab order and the a11y tree even
    // where the CSS visibility rule has not applied yet (e.g. during the
    // close transition).
    drawer.setAttribute("inert", "");
    if (overlay) overlay.setAttribute("data-state", "closed");

    if (!qsa('.drawer[data-state="open"]').length) {
      document.body.style.overflow = "";
    }

    qsa('[data-open-drawer="' + id + '"]').forEach(function (t) {
      t.setAttribute("aria-expanded", "false");
    });

    var back = drawerReturnFocus[id];
    if (back && document.contains(back)) back.focus();
    drawerReturnFocus[id] = null;
  }

  function closeAllDrawers() {
    qsa('.drawer[data-state="open"]').forEach(function (d) { closeDrawer(d.id); });
  }

  // Mark every drawer inert on load so nothing inside a closed panel is
  // reachable by Tab before it has ever been opened.
  qsa(".drawer").forEach(function (d) {
    if (d.getAttribute("data-state") !== "open") d.setAttribute("inert", "");
  });

  document.addEventListener("click", function (e) {
    var openTrigger = e.target.closest("[data-open-drawer]");
    if (openTrigger) {
      e.preventDefault();
      openDrawer(openTrigger.getAttribute("data-open-drawer"), openTrigger);
      return;
    }
    var closeTrigger = e.target.closest("[data-close-drawer]");
    if (closeTrigger) {
      e.preventDefault();
      closeDrawer(closeTrigger.getAttribute("data-close-drawer"));
      return;
    }
    if (e.target.closest(".drawer__overlay")) closeAllDrawers();
  });

  document.addEventListener("keydown", function (e) {
    var open = qs('.drawer[data-state="open"]');
    if (!open) return;

    if (e.key === "Escape") { e.preventDefault(); closeAllDrawers(); return; }

    if (e.key === "Tab") {
      var items = focusableIn(open);
      if (!items.length) { e.preventDefault(); return; }
      var first = items[0];
      var last = items[items.length - 1];
      if (!open.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Accordion ---------- */
  document.addEventListener("click", function (e) {
    var trigger = e.target.closest(".accordion__trigger");
    if (!trigger) return;
    var expanded = trigger.getAttribute("aria-expanded") === "true";
    var panel = document.getElementById(trigger.getAttribute("aria-controls"));
    var group = trigger.closest(".accordion");
    var closeSiblings = group && group.hasAttribute("data-single-open");

    if (closeSiblings) {
      qsa(".accordion__trigger", group).forEach(function (t) {
        if (t !== trigger) {
          t.setAttribute("aria-expanded", "false");
          var p = document.getElementById(t.getAttribute("aria-controls"));
          if (p) p.setAttribute("data-open", "false");
        }
      });
    }

    trigger.setAttribute("aria-expanded", String(!expanded));
    if (panel) panel.setAttribute("data-open", String(!expanded));
  });

  /* ---------- Scroll reveal ---------- */
  var revealItems = qsa("[data-reveal]");
  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (revealItems.length && "IntersectionObserver" in window && !reduceMotion) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    revealItems.forEach(function (el) { io.observe(el); });
  } else {
    revealItems.forEach(function (el) { el.classList.add("is-visible"); });
  }

  /* ---------- Sticky add-to-cart visibility ---------- */
  var atcButton = qs("[data-add-to-cart]");
  var stickyAtc = qs("[data-sticky-add-to-cart]");
  if (atcButton && stickyAtc && "IntersectionObserver" in window) {
    var atcObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var show = !entry.isIntersecting;
          stickyAtc.setAttribute("data-visible", String(show));
          // Keep the offscreen bar out of the tab order and the a11y tree.
          stickyAtc.setAttribute("aria-hidden", String(!show));
          if (show) stickyAtc.removeAttribute("inert");
          else stickyAtc.setAttribute("inert", "");
        });
      },
      { threshold: 0 }
    );
    atcObserver.observe(atcButton);
  }

  document.addEventListener("click", function (e) {
    var trigger = e.target.closest("[data-sticky-atc-submit]");
    if (!trigger || trigger.disabled) return;
    var mainForm = qs("[data-main-add-to-cart]");
    if (!mainForm) return;
    if (typeof mainForm.requestSubmit === "function") mainForm.requestSubmit();
    else mainForm.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-quantity-step]");
    if (!btn) return;
    var wrapper = btn.closest("[data-quantity-wrapper]");
    var input = wrapper && wrapper.querySelector("input[type='number']");
    if (!input) return;

    var step = parseInt(btn.getAttribute("data-quantity-step"), 10);
    var min = parseInt(input.getAttribute("min"), 10);
    if (isNaN(min)) min = 1;
    var value = Math.max(min, (parseInt(input.value, 10) || min) + step);

    // Respect the stock ceiling when the template knows one, instead of letting
    // the shopper count up and discover the limit as a failed request.
    var max = parseInt(input.getAttribute("max"), 10);
    if (!isNaN(max)) value = Math.min(max, value);

    if (String(value) === String(input.value)) return;
    input.value = value;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---------- Cart (AJAX) ---------- */
  var cartDrawerId = "CartDrawer";
  var cartSectionIds = "cart-drawer,main-cart";

  function updateCartCount(count) {
    if (typeof count !== "number" || isNaN(count)) return;
    qsa("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* Record enough about the focused control to find its replacement after the
     cart subtree is re-rendered. */
  function captureCartFocus() {
    var active = document.activeElement;
    if (!active || active === document.body || !active.closest) return null;

    if (active.matches("[data-cart-quantity-input]")) {
      return { key: active.getAttribute("data-key"), type: "input" };
    }
    var stepBtn = active.closest("[data-quantity-step]");
    if (stepBtn) {
      var wrap = stepBtn.closest("[data-quantity-wrapper]");
      var input = wrap && wrap.querySelector("[data-cart-quantity-input]");
      if (input) {
        return {
          key: input.getAttribute("data-key"),
          type: "step",
          step: stepBtn.getAttribute("data-quantity-step")
        };
      }
    }
    if (active.closest("[data-checkout-form]")) return { type: "checkout" };
    return null;
  }

  function restoreCartFocus(mark) {
    if (!mark) return;

    if (mark.type === "checkout") {
      var checkoutBtn = qs('[data-checkout-form] [type="submit"]');
      if (checkoutBtn) checkoutBtn.focus();
      return;
    }

    var input = qs('[data-cart-quantity-input][data-key="' + mark.key + '"]');
    // The line may be gone entirely (quantity taken to zero). Fall back to the
    // drawer close button so focus does not land on <body>.
    if (!input) {
      var fallback = qs("#" + cartDrawerId + '[data-state="open"] [data-close-drawer]') || qs("[data-cart-page] h1");
      if (fallback) {
        if (!fallback.hasAttribute("tabindex") && fallback.tagName === "H1") fallback.setAttribute("tabindex", "-1");
        fallback.focus();
      }
      return;
    }

    if (mark.type === "input") {
      input.focus();
      try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) { /* number inputs refuse this in some browsers */ }
      return;
    }

    var wrapper = input.closest("[data-quantity-wrapper]");
    var btn = wrapper && wrapper.querySelector('[data-quantity-step="' + mark.step + '"]');
    (btn || input).focus();
  }

  function swapFragment(sectionHtml, selector) {
    if (!sectionHtml) return null;
    var temp = document.createElement("div");
    temp.innerHTML = sectionHtml;
    var newEl = temp.querySelector(selector);
    var oldEl = document.querySelector(selector);
    if (!newEl || !oldEl) return null;
    // Carry the open/closed state across the swap, otherwise re-rendering the
    // drawer while it is open snaps it shut.
    if (oldEl.hasAttribute("data-state")) {
      newEl.setAttribute("data-state", oldEl.getAttribute("data-state"));
      newEl.setAttribute("aria-hidden", oldEl.getAttribute("aria-hidden") || "true");
      if (oldEl.hasAttribute("inert")) newEl.setAttribute("inert", "");
    }
    oldEl.replaceWith(newEl);
    return newEl;
  }

  function applyCartSections(sections) {
    if (!sections) return;
    var focusMark = captureCartFocus();

    if (sections["cart-drawer"]) {
      var newDrawer = swapFragment(sections["cart-drawer"], "#" + cartDrawerId);
      swapFragment(sections["cart-drawer"], '[data-drawer-overlay="' + cartDrawerId + '"]');
      if (newDrawer && newDrawer.hasAttribute("data-cart-count-value")) {
        updateCartCount(parseInt(newDrawer.getAttribute("data-cart-count-value"), 10));
      }
    }
    if (sections["main-cart"] && document.querySelector("[data-cart-page]")) {
      swapFragment(sections["main-cart"], "[data-cart-page]");
    }

    restoreCartFocus(focusMark);
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  function refreshCartFragments() {
    return fetch("/?sections=" + cartSectionIds)
      .then(function (r) { return r.json(); })
      .then(applyCartSections)
      .catch(function () {});
  }

  /* Read a human-readable reason out of a Shopify cart API error payload. */
  function cartErrorMessage(result) {
    if (!result) return null;
    if (result.description) return result.description;
    if (result.message) return result.message;
    if (result.errors) {
      if (typeof result.errors === "string") return result.errors;
      var key = Object.keys(result.errors)[0];
      if (key) return [].concat(result.errors[key]).join(" ");
    }
    return null;
  }

  /* /cart/change.js answers a rejected change with HTTP 422 and a JSON body
     carrying `status`. That body parses cleanly, so without this check the
     success branch runs on a failure. */
  function isCartError(payload) {
    return !payload || typeof payload.status !== "undefined" || typeof payload.item_count === "undefined";
  }

  function showAddError(form, message) {
    var slot = form && form.querySelector("[data-atc-error]");
    if (slot) {
      slot.textContent = message;
      slot.hidden = false;
      slot.scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
    } else {
      toast(message, true);
    }
  }

  function clearAddError(form) {
    var slot = form && form.querySelector("[data-atc-error]");
    if (slot) { slot.textContent = ""; slot.hidden = true; }
  }

  document.addEventListener("submit", function (e) {
    var form = e.target.closest('form[action^="/cart/add"]');
    if (!form) return;
    e.preventDefault();

    clearAddError(form);

    var submitBtn = form.querySelector('[type="submit"]');
    var stickyBtn = qs("[data-sticky-atc-submit]");
    setLoading(submitBtn, true);
    setLoading(stickyBtn, true);

    var body = new FormData(form);
    // Ask Shopify to render the cart sections as part of the same request.
    body.append("sections", cartSectionIds);
    body.append("sections_url", window.location.pathname);

    fetch("/cart/add.js", {
      method: "POST",
      body: body,
      headers: { Accept: "application/json" }
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (res) {
        // Shopify returns HTTP 422 with a `description` when a line cannot be
        // added (out of stock, quantity above available, etc).
        if (!res.ok || res.data.status) {
          throw new Error(cartErrorMessage(res.data) || "Sorry, that could not be added to your cart.");
        }
        applyCartSections(res.data.sections);

        var themeSettings = window.theme || {};
        if (themeSettings.cartType === "page") {
          window.location.href = "/cart";
          return;
        }
        // The sections payload does not include the cart total count, so pull
        // it only when the drawer did not carry it.
        if (!qs("#" + cartDrawerId + "[data-cart-count-value]")) {
          fetch("/cart.js")
            .then(function (r) { return r.json(); })
            .then(function (cart) { updateCartCount(cart.item_count); })
            .catch(function () {});
        }
        openDrawer(cartDrawerId, submitBtn);
        announce("Added to your cart");
      })
      .catch(function (err) {
        showAddError(form, err && err.message ? err.message : "Sorry, that could not be added to your cart.");
      })
      .then(function () {
        // setLoading restores whatever disabled state the button had before the
        // request, so a sold-out variant stays sold out. See defect 15.
        setLoading(submitBtn, false);
        setLoading(stickyBtn, false);
      });
  });

  /* ---------- Remove a cart line ---------- */
  document.addEventListener("click", function (e) {
    var removeBtn = e.target.closest("[data-cart-remove]");
    if (!removeBtn) return;
    e.preventDefault();
    var key = removeBtn.getAttribute("data-cart-remove");
    removeBtn.setAttribute("disabled", "disabled");

    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: 0, sections: cartSectionIds })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (isCartError(cart)) throw new Error(cartErrorMessage(cart) || "");
        updateCartCount(cart.item_count);
        if (cart.sections) applyCartSections(cart.sections);
        else refreshCartFragments();
        announce("Item removed from your cart");
      })
      .catch(function (err) {
        toast((err && err.message) || "We could not update your cart. Please try again.", true);
        removeBtn.removeAttribute("disabled");
        refreshCartFragments();
      });
  });

  /* ---------- Cart line quantity change (drawer + page) ----------
     Debounced PER LINE. A single shared timer meant that changing line A and
     then line B inside the debounce window cancelled line A's request, so the
     first change was silently dropped. */
  var quantityTimers = {};

  document.addEventListener("change", function (e) {
    var input = e.target.closest("[data-cart-quantity-input]");
    if (!input) return;
    var key = input.getAttribute("data-key");
    if (!key) return;

    var quantity = Math.max(0, parseInt(input.value, 10) || 0);
    var max = parseInt(input.getAttribute("max"), 10);
    if (!isNaN(max) && quantity > max) {
      quantity = max;
      input.value = max;
      toast("Only " + max + " available.", true);
    }

    var wrapper = input.closest("[data-quantity-wrapper]");
    if (wrapper) wrapper.setAttribute("data-updating", "true");

    clearTimeout(quantityTimers[key]);
    quantityTimers[key] = setTimeout(function () {
      delete quantityTimers[key];
      fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity, sections: cartSectionIds })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (isCartError(cart)) throw new Error(cartErrorMessage(cart) || "");
          updateCartCount(cart.item_count);
          if (cart.sections) applyCartSections(cart.sections);
          else refreshCartFragments();
          announce(quantity === 0 ? "Item removed from your cart" : "Cart updated");
        })
        .catch(function (err) {
          toast((err && err.message) || "We could not update that quantity. Please try again.", true);
          // Pull the server's truth back so the input never shows a number the
          // cart does not actually hold.
          refreshCartFragments();
        });
    }, 400);
  });

  /* ---------- Checkout: prevent double submission ---------- */
  function resetCheckoutButtons() {
    qsa('[data-checkout-form] [type="submit"]').forEach(function (btn) {
      delete btn.dataset.submitted;
      btn.classList.remove("is-loading");
      btn.removeAttribute("aria-busy");
    });
  }

  document.addEventListener("submit", function (e) {
    var form = e.target.closest("[data-checkout-form]");
    if (!form) return;
    var btn = form.querySelector('[type="submit"]');
    if (!btn) return;
    if (btn.dataset.submitted === "true") { e.preventDefault(); return; }
    btn.dataset.submitted = "true";
    btn.classList.add("is-loading");
    btn.setAttribute("aria-busy", "true");
    // Do NOT set disabled here: a disabled submit button is excluded from the
    // form submission and Shopify would not receive name="checkout".
  });

  /* A page restored from the back/forward cache comes back byte-for-byte as it
     was left — including a Checkout button frozen mid-submit. Without this a
     shopper who taps Checkout and then presses Back finds a permanently dead
     button and no way forward short of a hard reload. */
  window.addEventListener("pageshow", function (event) {
    if (!event.persisted) return;
    resetCheckoutButtons();
    if (!qsa('.drawer[data-state="open"]').length) document.body.style.overflow = "";
  });

  /* ---------- Header: transparent -> glass on scroll ---------- */
  var header = qs(".header");
  if (header) {
    var lastScrolled = null;
    var ticking = false;
    var applyScrollState = function () {
      var scrolled = window.scrollY > 20;
      if (scrolled !== lastScrolled) {
        header.setAttribute("data-scrolled", String(scrolled));
        lastScrolled = scrolled;
      }
      ticking = false;
    };
    applyScrollState();
    window.addEventListener("scroll", function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(applyScrollState); }
    }, { passive: true });
  }
})();
